import { apiClient } from './api'

export type PlatformSettings = {
  id: string
  general: {
    platform_name: string
    platform_description: string
    platform_logo_url?: string
  }
  database: {
    pool_size: number
    timeout: number
    max_overflow: number
  }
  security: {
    session_timeout_minutes: number
    max_login_attempts: number
    force_2fa_superusers: boolean
    password_min_length: number
    password_require_special: boolean
    jwt_expire_hours: number
  }
  email: {
    smtp_host?: string
    smtp_port: number
    smtp_username?: string
    smtp_password?: string
    smtp_use_tls: boolean
    email_from_address?: string
    email_from_name?: string
  }
  notifications: {
    enable_email_notifications: boolean
    enable_system_alerts: boolean
    alert_email_recipients: string[]
  }
  regional: {
    default_timezone: string
    default_language: string
    default_currency: string
    date_format: string
    time_format: string
  }
  api: {
    rate_limit_per_minute: number
    enable_cors: boolean
    cors_origins: string[]
  }
  system: {
    maintenance_mode: boolean
    maintenance_message?: string
    log_level: string
    log_retention_days: number
    enable_audit_logs: boolean
  }
  backup: {
    backup_enabled: boolean
    backup_frequency_hours: number
    backup_retention_days: number
  }
  limits: {
    max_organizations: number
    max_users_per_org: number
    max_resources_per_org: number
  }
  features: Record<string, boolean>
  hub: {
    allowed_org_types: string[]
  }
  asaas?: {
    sandbox?: {
      base_url?: string | null
      api_key_set?: boolean
      webhook_token_set?: boolean
    }
    production?: {
      base_url?: string | null
      api_key_set?: boolean
      webhook_token_set?: boolean
    }
  }
  metadata: {
    created_at: string
    updated_at: string
    updated_by?: string
  }
}

export type SettingsUpdateData = {
  general?: Partial<PlatformSettings['general']>
  database?: Partial<PlatformSettings['database']>
  security?: Partial<PlatformSettings['security']>
  email?: Partial<PlatformSettings['email']>
  notifications?: Partial<PlatformSettings['notifications']>
  regional?: Partial<PlatformSettings['regional']>
  api?: Partial<PlatformSettings['api']>
  system?: Partial<PlatformSettings['system']>
  backup?: Partial<PlatformSettings['backup']>
  limits?: Partial<PlatformSettings['limits']>
  features?: Record<string, boolean>
  hub?: {
    allowed_org_types: string[]
  }
  asaas?: {
    sandbox?: {
      base_url?: string | null
      api_key?: string
      webhook_token?: string
    }
    production?: {
      base_url?: string | null
      api_key?: string
      webhook_token?: string
    }
  }
}

export type EmailTestData = {
  recipient_email?: string
}

export type EmailTestResponse = {
  success: boolean
  message: string
  smtp_host: string
  smtp_port: number
}

export type SettingsExportResponse = {
  exported_at: string
  exported_by: string
  settings: PlatformSettings
}

const settingsApi = {
  // Get platform settings
  getSettings: (): Promise<PlatformSettings> => {
    return apiClient.get('/api/v1/settings')
  },

  // Update platform settings
  updateSettings: (data: SettingsUpdateData): Promise<PlatformSettings> => {
    return apiClient.put('/api/v1/settings', data)
  },

  // Test email configuration
  testEmail: (data: EmailTestData): Promise<EmailTestResponse> => {
    return apiClient.post('/api/v1/settings/test-email', data)
  },

  // Export settings
  exportSettings: (): Promise<SettingsExportResponse> => {
    return apiClient.get('/api/v1/settings/export')
  },

  // Common timezone options
  getTimezones: (): string[] => {
    return [
      'America/Sao_Paulo',
      'America/New_York',
      'America/Los_Angeles',
      'America/Chicago',
      'America/Denver',
      'Europe/London',
      'Europe/Paris',
      'Europe/Berlin',
      'Asia/Tokyo',
      'Asia/Shanghai',
      'Asia/Kolkata',
      'Australia/Sydney',
      'UTC',
    ]
  },

  // Common language options
  getLanguages: (): { code: string; name: string }[] => {
    return [
      { code: 'pt-BR', name: 'Português (Brasil)' },
      { code: 'en-US', name: 'English (US)' },
      { code: 'es-ES', name: 'Español' },
      { code: 'fr-FR', name: 'Français' },
      { code: 'de-DE', name: 'Deutsch' },
      { code: 'it-IT', name: 'Italiano' },
      { code: 'ja-JP', name: '日本語' },
      { code: 'zh-CN', name: '中文 (简体)' },
    ]
  },

  // Common currency options
  getCurrencies: (): { code: string; name: string }[] => {
    return [
      { code: 'BRL', name: 'Real Brasileiro (R$)' },
      { code: 'USD', name: 'US Dollar ($)' },
      { code: 'EUR', name: 'Euro (€)' },
      { code: 'GBP', name: 'British Pound (£)' },
      { code: 'JPY', name: 'Japanese Yen (¥)' },
      { code: 'CAD', name: 'Canadian Dollar (C$)' },
      { code: 'AUD', name: 'Australian Dollar (A$)' },
    ]
  },

  // Log level options
  getLogLevels: (): string[] => {
    return ['DEBUG', 'INFO', 'WARNING', 'ERROR', 'CRITICAL']
  },
}

export { settingsApi }
